import React from 'react';
import { inject, observer } from 'mobx-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import withLocalization from '~/hoc/withLocalization';
import Checkbox from '~/components/CustomCheckbox/CustomCheckbox';

/**
 * RosterOverrideSettings — "Override existing shifts" / "Override auto time recording" checkboxes
 * for roster shift publishing. Self-gated by
 * basicRules.workPlanSettings.allow_optional_roster_shift_override (renders nothing when off).
 * The "Override auto time recording" checkbox additionally requires
 * loginRules.allowShiftToEnableDisableAutoTimesheet.
 *
 * Props:
 *  - overrideShift {bool}, overrideAutoTimeRecording {bool}
 *  - onChange({ overrideShift, overrideAutoTimeRecording })
 */
const RosterOverrideSettings = (props) => {
    const { t, commonStore, overrideShift = true, overrideAutoTimeRecording = true, onChange } = props;

    const clientData = commonStore && commonStore.config && commonStore.config.client && commonStore.config.client.data;
    const allowOverride = !!(clientData && clientData.basicRules && clientData.basicRules.workPlanSettings && clientData.basicRules.workPlanSettings.allow_optional_roster_shift_override);
    const allowShiftAutoTimesheetToggle = !!(clientData && clientData.loginRules && clientData.loginRules.allowShiftToEnableDisableAutoTimesheet);

    if (!allowOverride) return null;

    // "Override Auto" can only be on when "Override existing shifts" is on (dependency lives here).
    const handleOverrideShift = (checked) => {
        onChange({
            overrideShift: checked,
            overrideAutoTimeRecording: checked ? overrideAutoTimeRecording !== false : false,
        });
    };
    const handleOverrideAuto = (checked) => {
        onChange({
            overrideShift: overrideShift !== false,
            overrideAutoTimeRecording: checked,
        });
    };

    return (
        <div className="mt-2 mb-2 d-flex align-items-center flex-wrap">
            <div className="me-3 d-flex align-items-center">
                <Checkbox
                    isChecked={overrideShift !== false}
                    onChange={handleOverrideShift}
                    label={t('Override existing shifts')}
                />
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-override-shift">{t('When on, existing shifts on the roster days are replaced according to the roster plan. When off, only days without any shift get a new shift.')}</Tooltip>}
                >
                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                </OverlayTrigger>
            </div>
            {allowShiftAutoTimesheetToggle && (
                <div className="me-2 d-flex align-items-center">
                    <Checkbox
                        isChecked={overrideShift !== false && overrideAutoTimeRecording !== false}
                        onChange={handleOverrideAuto}
                        label={t('Override auto time recording')}
                        disabled={overrideShift === false}
                    />
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="tooltip-override-auto">{t('When on, overridden shifts also take the auto time recording from the roster plan. When off, existing shifts keep their current auto time recording.')}</Tooltip>}
                    >
                        <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                    </OverlayTrigger>
                </div>
            )}
        </div>
    );
};

export default inject('commonStore')(withLocalization(observer(RosterOverrideSettings)));
